import { useState } from "react"
import { Box, Grid, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { RequestTabledRepeat } from "lib/@components/request-tabled-repeat"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import {
    getHistoricalMaintenanceCategories,
    getHistoricalMaintenanceTasks,
    getMaintenanceCategories,
    getMaintenanceTasks,
} from "../../controller/maintenance-library"
import { TaskRow } from "./task-row"
import { CategoryItem } from "./category-item"
import { CollapsibleSearchSection } from "./collapsible-search-section"

const EMBEDDED_TAKE = 200

const EMBEDDED_TASK_HEADERS = [
    { title: "Heading", fieldKey: "heading" },
    { title: "Classification", align: "center" },
    { title: "Frequency", align: "center" },
    { title: "Skill Set", align: "center" },
    { title: "Timing", align: "center" },
]

const TASK_HEADERS = [
    { title: "Heading" },
    { title: "Classification" },
    { title: "Frequency" },
    { title: "Skill Set" },
    { title: "Actions", align: "center" },
    { title: "Notes", align: "center" },
    { title: "Author", align: "center" },
]

export function SearchResults({ search, categoryId }) {
    const { historicalMode, libraryAsOf, embedded } = useBoundContext()
    const [skip, setSkip] = useState(0)
    const take = embedded ? EMBEDDED_TAKE : 25

    const currentTasks = getMaintenanceTasks.useResults({
        search,
        categoryId,
        skip,
        take,
        orderStmt: ["heading"],
        excludeDrafts: embedded,
    })
    const historicalTasks = getHistoricalMaintenanceTasks.useResults(
        historicalMode
            ? {
                  search,
                  categoryId,
                  skip,
                  take,
                  orderStmt: ["heading"],
                  asOf: libraryAsOf,
              }
            : undefined
    )
    const { items = [], totalCount = 0 } = (historicalMode ? historicalTasks : currentTasks) ?? {}

    const currentCategories = getMaintenanceCategories.useResults({
        search,
        rootCategoryId: categoryId,
    })
    const historicalCategories = getHistoricalMaintenanceCategories.useResults(
        historicalMode
            ? {
                  search,
                  rootCategoryId: categoryId,
                  asOf: libraryAsOf,
              }
            : undefined
    )
    const categories = historicalMode ? historicalCategories : currentCategories
    const taskHeaders = embedded ? EMBEDDED_TASK_HEADERS : TASK_HEADERS
    const taskResultsTitle = embedded ? "Matching Tasks" : "Matching Components"
    const emptyTaskResultsMessage = embedded
        ? `No tasks found matching "${search}"`
        : `No components found matching "${search}"`

    return (
        <Box>
            <Typography variant={embedded ? "h6" : "subtitle1"} sx={{ mb: 2 }}>
                Search Results for "{search}"
            </Typography>

            {categories?.length > 0 && (
                <CollapsibleSearchSection
                    title="Matching Documents & Folders"
                    collapsible={embedded}
                    defaultOpen={false}
                >
                    <Grid container spacing={2}>
                        <Repeat list={categories} item={<CategoryItem />} />
                    </Grid>
                </CollapsibleSearchSection>
            )}

            <CollapsibleSearchSection
                title={taskResultsTitle}
                collapsible={embedded}
                defaultOpen
                sx={embedded ? undefined : { mb: 0 }}
            >
                <RequestTabledRepeat
                    items={items}
                    total={totalCount}
                    setSkip={setSkip}
                    pageSize={take}
                    refreshKey={search + categoryId}
                    tableHeaders={taskHeaders}
                    itemPass="item"
                    itemComponent={<TaskRow />}
                    alternating={false}
                    emptyMessage={emptyTaskResultsMessage}
                />
            </CollapsibleSearchSection>
        </Box>
    )
}
